import React, { useRef, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { defectAPI, imageAPI } from "../../api";
import { WdContext } from "../../context/WdProvider";
import EndlessScrollImg from "../endless-scroll-img/EndlessScrollImg";
import AlertPopup from "../alert-popup/AlertPopup";
import WplButton from "../wpl-button/WplButton";
import WplPopup from "../wpl-popup-window/WplPopup";
import WplInput from "../wpl-input/WplInput";
import WplSelectInput from "../wpl-select-input/WplSelectInput";
import loading from "../../assets/loading.svg";
import cross from "../../assets/img/clear-24px.svg";
import "./defect.css";

export default function Defect({ showCloseButton = false, close }) {
  const { defects, setDefects, selectedDefect, setSelectedDefect } = useContext(WdContext);
  const [previousDefects, setPreviousDefects] = useState([]);
  const [largeDefectImg, setLargeDefectImage] = useState("");
  const [smallDefectImg, setSmallDefectImage] = useState("");
  const [showLargeImg, setShowLargeImg] = useState(false);
  const [defectBox, setDefectBox] = useState(null);
  const [defectBoxLoading, setDefectBoxLoading] = useState(false);
  const [updatingDefectID, setUpdatingDefectID
  ] = useState(null);
  const [notificationPopup, setNotificationPopup] = useState({
    show: false, title: "", status: "", message: ""
  })

  const [editMode, setEditMode] = useState(false)
  const [updatedDefect, setUpdatedDefect] = useState({
    severity: selectedDefect.severity || "",
    defectType: selectedDefect.defectType || "",
    surface: selectedDefect.surface || "",
    repairRecommendation: selectedDefect.repairRecommendation || "",
    repairComment: selectedDefect.repairComment || "",
    distanceFromHub: selectedDefect.distanceFromHub || "",
    layer: selectedDefect.layer || "",
    customerNote: selectedDefect.customerNote || "",
    budgetNote: selectedDefect.budgetNote || "",
    downtimeNote: selectedDefect.downtimeNote || "",
    blade: selectedDefect.blade || ""
  });

  const [confirmPopup, setConfirmPopup] = useState({
    show: false,
    action: null,
    message: "",
  });

  const outerWrapper = useRef();

  let { turbine_id } = useParams();
  turbine_id = parseInt(turbine_id);

  useEffect(() => {
    if (showLargeImg) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showLargeImg]);

  useEffect(() => {
    if (!selectedDefect?.id) return;

    const updated = defects.find(d => d.id === selectedDefect.id);

    if (updated) {
      setSelectedDefect(updated);

      setUpdatedDefect({
        severity: updated.severity || "",
        defectType: updated.defectType || "",
        surface: updated.surface || "",
        repairRecommendation: updated.repairRecommendation || "",
        repairComment: updated.repairComment || "",
        distanceFromHub: updated.distanceFromHub || "",
        layer: updated.layer || "",
        customerNote: updated.customerNote || "",
        budgetNote: updated.budgetNote || "",
        downtimeNote: updated.downtimeNote || "",
        blade: updated.blade || ""
      });
    }

    const prev = defects
      .filter(d => d.turbineName === selectedDefect.turbineName)
      .filter(d => d.inspectionDate !== selectedDefect.inspectionDate)
      .filter(d => d.blade === updated?.blade)
      .filter(d => d.surface === updated?.surface)
      .filter(d => Math.abs(d.distanceFromHub - updated?.distanceFromHub) < 1.5);

    setPreviousDefects(prev);

  }, [defects, selectedDefect?.id]);

  useEffect(() => {
    if (!selectedDefect.id || !showLargeImg || largeDefectImg === loading) return;

    setLargeDefectImage(loading);
    imageAPI.getDefectBladeImageUrl(selectedDefect.id).then(setLargeDefectImage);
  }, [selectedDefect.id, showLargeImg]);

  // Fetched as soon as the defect is selected so the box is usually ready by
  // the time the image popup opens.
  useEffect(() => {
    let cancelled = false;
    setDefectBox(null);
    if (!selectedDefect.reportDefectId) return;

    setDefectBoxLoading(true);
    defectAPI.getDefectPosition(selectedDefect.reportDefectId)
      .then(pos => {
        if (!cancelled) setDefectBox(pos && pos.width > 0 && pos.height > 0 ? pos : null);
      })
      .catch(() => {
        if (!cancelled) setDefectBox(null);
      })
      .finally(() => {
        if (!cancelled) setDefectBoxLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDefect.reportDefectId]);

  useEffect(() => {
    if (!selectedDefect.id || smallDefectImg === loading) return;
    setSmallDefectImage(loading);
    imageAPI.getDefectImageUrl(selectedDefect.id).then(setSmallDefectImage);
  }, [selectedDefect.id]);


  const handleSaveDefect = async () => {
    setUpdatingDefectID(selectedDefect.id);
    const {
      distanceFromHub,
      severity,
      defectType,
      surface,
      repairRecommendation,
      repairComment,
      layer,
      customerNote,
      budgetNote,
      downtimeNote,
      blade
    } = updatedDefect;

    let parsedDistance = null;
    if (String(distanceFromHub).trim() !== "") {
      parsedDistance = parseFloat(String(distanceFromHub).replace(",", "."));
      if (isNaN(parsedDistance)) parsedDistance = null;
    }

    const payload = {
      distance: parsedDistance,
      severity,
      defect_type: defectType,
      surface,
      repair_action: repairRecommendation,
      repair_comment: repairComment,
      layer,
      customer_note: customerNote,
      budget_note: budgetNote,
      downtime_note: downtimeNote,
      blade
    };
    try {
      const res = await defectAPI.editDefect(selectedDefect.id, payload);
      if (res) {
        const updatedFromServer = { ...selectedDefect, ...updatedDefect };
        setDefects(prev =>
          prev.map(d => (d.id === selectedDefect.id ? updatedFromServer : d))
        );
        setSelectedDefect(updatedFromServer);


        setEditMode(false);
        setNotificationPopup({
          show: true,
          title: "Success!",
          status: "success",
          message: "Defect updated successfully!",
        });
      }

    } catch (err) {
      console.error(err);
      setNotificationPopup({
        show: true,
        title: "Something went wrong...",
        status: "wrong",
        message: err.response?.data

      });
    } finally {
      setEditMode(false);
      setUpdatingDefectID(null);
    }
  };


  const handleInputChange = (field, value) => {
    setUpdatedDefect(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirmAction = (action, message) => {
    setConfirmPopup({ show: true, action, message });
  };

  const deleteDefect = async (id) => {
    setUpdatingDefectID(selectedDefect.id);
    try {
      await defectAPI.deleteDefectAdmin(id);

      setDefects(prev => prev.filter(d => d.id !== id));

      setNotificationPopup({
        show: true,
        title: "Deleted",
        status: "success",
        message: "Defect deleted successfully.",
      });
    } catch (error) {
      console.log('Error deleting defect:', error.response || error);
      setNotificationPopup({
        show: true,
        title: "Error",
        status: "wrong",
        message: error.response?.data
      });
    } finally {
      setUpdatingDefectID(null);
    }
  };

  return (
    <div className="defect-wrapper" ref={outerWrapper}>
      <WplPopup className="large-img-popup" closePopup={setShowLargeImg} showPopup={showLargeImg}>
        <EndlessScrollImg wideImg={largeDefectImg} closeUpImg={smallDefectImg} box={defectBox} boxLoading={defectBoxLoading} />
      </WplPopup>
      <div className="header">
        <h1>Defect {selectedDefect.id}</h1>
        <div className="spacer">&nbsp;</div>
        {showCloseButton && <img src={cross} alt="close" onClick={close} />}
      </div>
      <div className="info-wrapper">
        <div className="defect-info-wrapper">
          <table className={updatingDefectID === selectedDefect.id ? "updating-defect" : ""}>
            <tbody>
              <tr>
                <th>Windfarm</th>
                <td>{selectedDefect.windfarm}</td>
              </tr>
              <tr>
                <th>Turbine</th>
                <td>{selectedDefect.turbineName}</td>
              </tr>
              <tr>
                <th>Inspection date</th>
                <td>
                  {selectedDefect.inspectionDate}
                </td>
              </tr>
              <tr>
                <th>Blade</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.blade || ""}
                      onChange={e => handleInputChange("blade", e.target.value)}
                    />
                  ) : (
                    selectedDefect.blade
                  )}
                </td>
              </tr>
              <tr>
                <th>Defect type</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.defectType || ""}
                      onChange={e => handleInputChange("defectType", e.target.value)}
                    />
                  ) : (
                    selectedDefect.defectType
                  )}
                </td>
              </tr>
              <tr>
                <th>Layer</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.layer || ""}
                      onChange={e => handleInputChange("layer", e.target.value)}
                    />
                  ) : (
                    selectedDefect.layer
                  )}
                </td>
              </tr>
              <tr>
                <th>Surface</th>
                <td>
                  {editMode ? (
                    <WplSelectInput
                      options={['LE', 'SS', 'TE', 'PS']}
                      value={updatedDefect.surface || ""}
                      onChange={value => handleInputChange("surface", value)}
                      placeholder="Select a turbine"
                      inputClassName='defect-select'
                    />
                  ) : (
                    selectedDefect.surface
                  )}
                </td>
              </tr>
              <tr>
                <th>Severity</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.severity || ""}
                      onChange={e => handleInputChange("severity", e.target.value)}
                    />
                  ) : (
                    selectedDefect.severity
                  )}
                </td>
              </tr>
              <tr>
                <th>Defect comment</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.customerNote || ""}
                      onChange={e => handleInputChange("customerNote", e.target.value)}
                    />
                  ) : (
                    selectedDefect.customerNote
                  )}
                </td>
              </tr>
              <tr>
                <th>Budget</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.budgetNote || ""}
                      onChange={e => handleInputChange("budgetNote", e.target.value)}
                    />
                  ) : (
                    selectedDefect.budgetNote
                  )}
                </td>
              </tr>
              <tr>
                <th>Downtime</th>
                <td>
                  {editMode ? (
                    <WplInput
                      value={updatedDefect.downtimeNote || ""}
                      onChange={e => handleInputChange("downtimeNote", e.target.value)}
                    />
                  ) : (
                    selectedDefect.downtimeNote
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="action-btn-container">
            <WplButton
              value={editMode ? "Cancel" : "Edit"}
              onClick={() => {
                if (editMode) {
                  setUpdatedDefect({
                    blade: selectedDefect.blade || "",
                    severity: selectedDefect.severity || "",
                    defectType: selectedDefect.defectType || "",
                    surface: selectedDefect.surface || "",
                    repairRecommendation: selectedDefect.repairRecommendation || "",
                    repairComment: selectedDefect.repairComment || "",
                    distanceFromHub: selectedDefect.distanceFromHub || "",
                    layer: selectedDefect.layer || "",
                    customerNote: selectedDefect.customerNote || "",
                    budgetNote: selectedDefect.budgetNote || "",
                    downtimeNote: selectedDefect.downtimeNote || "",
                  });
                  setEditMode(false);
                } else {
                  setEditMode(true);
                }
              }}
            />
            <WplButton
              value={editMode ? "Save" : "Delete"}
              red={!editMode}
              onClick={() => {
                if (editMode) {
                  handleConfirmAction(handleSaveDefect, "Are you sure you want to save changes?");
                } else {
                  handleConfirmAction(() => deleteDefect(selectedDefect.id), "Are you sure you want to delete this defect?");
                }
              }}
            />

          </div>
        </div>

        <div className="defect-info-wrapper img-wrapper">
          <img className="defect-img" src={smallDefectImg} onClick={() => setShowLargeImg(true)} alt="defect img" />
          {previousDefects.length > 0 && <p className="defect-subtitle">Previous</p>}
          <table>
            <tbody>
              {previousDefects
                .sort(
                  (a, b) =>
                    Math.abs(selectedDefect.distanceFromHub - a.distanceFromHub) -
                    Math.abs(selectedDefect.distanceFromHub - b.distanceFromHub)
                )
                .map((d) => (
                  <tr key={d.id}>
                    <td>{d.inspectionDate}</td>
                    <td>{d.defectType}</td>
                    <td>{d.layer}</td>
                    <td>{Math.round(Math.abs(d.distanceFromHub - selectedDefect.distanceFromHub) * 100) / 100}m</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <AlertPopup
          status="warning"
          title="Confirm Action"
          showPopup={confirmPopup.show}
          closePopup={() => setConfirmPopup({ show: false })}
          buttonText="Cancel"
          buttonStatus={editMode ? "wrong" : "success"}
          action={() => setConfirmPopup({ show: false })}
          buttonText2={editMode ? "Save" : "Delete"}
          buttonStatus2={editMode ? "success" : "wrong"}
          action2={() => {
            if (confirmPopup.action) confirmPopup.action();
            setConfirmPopup({ show: false });
          }}
        >
          {confirmPopup.message}
        </AlertPopup>

        <AlertPopup status={notificationPopup.status}
          title={notificationPopup.title}
          showPopup={notificationPopup.show}
          closePopup={() => setNotificationPopup({ show: false })}
          buttonText="Close"
          buttonStatus={notificationPopup.status}
          action={() => setNotificationPopup({ show: false })}
        >
          {notificationPopup.message}
        </AlertPopup>
      </div>
    </div>
  );
}
